import { PhoneCall, Mail, AlertTriangle } from "lucide-react";
import { PARENT_POLICY_CLAUSES, PARENT_POLICY_SUPPORT } from "../data/parentPolicyClauses";

/**
 * Prominent, brand-styled rendering of the 9 official Parent Policy clauses.
 * Used on Pay Now page + above the detailed Parent Policy.
 */
export default function ParentPolicyClauses({ compact = false }) {
    return (
        <div data-testid="parent-policy-clauses">
            <div className="flex items-start gap-3 p-4 sm:p-5 bg-red-50 border border-red-200 rounded-2xl mb-6">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div className="text-sm text-red-900 leading-relaxed">
                    <strong className="font-heading">Important — please read before enrolling.</strong>{" "}
                    These 9 terms are binding. Violation will lead to termination of service with no refund.
                </div>
            </div>

            <div className={`space-y-${compact ? "3" : "4"}`}>
                {PARENT_POLICY_CLAUSES.map((c) => (
                    <div
                        key={c.n}
                        data-testid={`parent-clause-${c.n}`}
                        className={`bg-white border border-slate-200 rounded-xl ${compact ? "p-4" : "p-5 sm:p-6"} shadow-sm`}
                    >
                        <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-[#1E3A8A] text-white font-heading font-bold flex items-center justify-center shrink-0 text-sm">
                                {c.n}
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className="font-heading font-bold text-[#0F172A] mb-1.5">{c.title}</h4>
                                <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{c.body}</p>
                                {c.bullets && (
                                    <ul className="mt-2.5 space-y-1 text-sm text-slate-700">
                                        {c.bullets.map((b) => (
                                            <li key={b} className="flex items-start gap-2">
                                                <span className="w-1 h-1 rounded-full bg-red-500 mt-2 shrink-0" />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {c.footer && (
                                    <p className="mt-2.5 text-sm text-slate-700 italic leading-relaxed">{c.footer}</p>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Support block */}
            <div className="mt-8 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white p-6 sm:p-7">
                <div className="text-xs font-semibold uppercase tracking-[0.25em] text-blue-200 mb-2">Support</div>
                <h4 className="font-heading font-bold text-xl mb-4">Need help or have questions?</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                    {PARENT_POLICY_SUPPORT.phones.map((p) => (
                        <a
                            key={p}
                            href={`tel:${p.replace(/\s+/g, "")}`}
                            data-testid={`support-phone-${p.replace(/\D/g, "")}`}
                            className="inline-flex items-center gap-2 px-3 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-semibold transition-colors"
                        >
                            <PhoneCall className="w-4 h-4 text-amber-300 shrink-0" />
                            {p}
                        </a>
                    ))}
                    <a
                        href={`mailto:${PARENT_POLICY_SUPPORT.email}`}
                        data-testid="support-email"
                        className="inline-flex items-center gap-2 px-3 py-2.5 bg-white/10 hover:bg-white/20 rounded-lg text-sm font-semibold transition-colors sm:col-span-2"
                    >
                        <Mail className="w-4 h-4 text-amber-300 shrink-0" />
                        {PARENT_POLICY_SUPPORT.email}
                    </a>
                </div>
                <div className="mt-4 text-xs text-blue-200 italic">— Regards, Anand Tutorials</div>
            </div>
        </div>
    );
}
